// src/StatsPlot.js
import React, { useMemo, useState, useCallback, useRef } from 'react';
import './StatsPlot.css';

const StatsPlot = ({
    statsHistory = [],
    width = 340,
    height = 140,
}) => {
    const padding = { top: 16, right: 12, bottom: 28, left: 28 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const [selectedStat, setSelectedStat] = useState('fieldPosition');
    const [isDragging, setIsDragging] = useState(false);
    const [hoverIndex, setHoverIndex] = useState(null);
    const svgRef = useRef(null);

    const numPoints = statsHistory.length;
    const xStep = numPoints > 1 ? chartWidth / (numPoints - 1) : 0;

    // Compute the displayed index (hover takes priority, otherwise last)
    const displayIndex = hoverIndex ?? (numPoints - 1);

    // Build paths and points for full history (for line chart)
    const { fieldPositionPath, pieceTiltPath, valuePath, fpPoints, ptPoints, valuePoints, hasData } = useMemo(() => {
        if (!statsHistory || statsHistory.length < 2) {
            return { fieldPositionPath: '', pieceTiltPath: '', valuePath: '', fpPoints: [], ptPoints: [], valuePoints: [], hasData: false };
        }

        // Map normalized values (-1 to 1) to y coordinates
        // -1 (P1 advantage) -> top, +1 (P2 advantage) -> bottom
        const yScale = (val) => {
            if (val === null || val === undefined) return null;
            const clamped = Math.max(-1, Math.min(1, val));
            return padding.top + ((clamped + 1) / 2) * chartHeight;
        };

        let fpPoints = [];
        let ptPoints = [];
        let valuePoints = [];

        statsHistory.forEach((entry, i) => {
            const x = padding.left + i * xStep;

            const fpY = yScale(entry.fieldPosition);
            fpPoints.push({ x, y: fpY, value: entry.fieldPosition });

            // Treat null pieceTilt as 0 (midline) for display purposes
            const ptVal = entry.pieceTilt ?? 0;
            const ptY = yScale(ptVal);
            ptPoints.push({ x, y: ptY, value: ptVal });

            // Add value estimate (winning probability)
            const valueY = yScale(entry.value);
            valuePoints.push({ x, y: valueY, value: entry.value });
        });

        // Convert points to SVG path (line)
        const toPath = (points) => {
            const validPoints = points.filter(p => p.y !== null);
            if (validPoints.length < 2) return '';
            return validPoints
                .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
                .join(' ');
        };

        return {
            fieldPositionPath: toPath(fpPoints),
            pieceTiltPath: toPath(ptPoints),
            valuePath: toPath(valuePoints),
            fpPoints,
            ptPoints,
            valuePoints,
            hasData: fpPoints.some(p => p.y !== null) || ptPoints.some(p => p.y !== null) || valuePoints.some(p => p.y !== null),
        };
    }, [statsHistory, chartWidth, chartHeight, padding.top, padding.left, xStep]);

    // Calculate cumulative stats up to displayIndex (recalculates when scrubbing)
    const cumulativeStats = useMemo(() => {
        if (!statsHistory || statsHistory.length < 2 || displayIndex < 0) {
            return null;
        }

        // Threshold to consider values as "midline" (about half a row on a 21-row board)
        const MIDLINE_THRESHOLD = 0.06;

        let fpP1Count = 0, fpP2Count = 0;
        let ptP1Count = 0, ptP2Count = 0;

        // Only count up to and including displayIndex
        for (let i = 0; i <= displayIndex && i < statsHistory.length; i++) {
            const entry = statsHistory[i];

            if (entry.fieldPosition !== null && entry.fieldPosition !== undefined) {
                // Exclude near-midline values
                if (entry.fieldPosition < -MIDLINE_THRESHOLD) fpP1Count++;
                else if (entry.fieldPosition > MIDLINE_THRESHOLD) fpP2Count++;
            }

            if (entry.pieceTilt !== null && entry.pieceTilt !== undefined) {
                // Exclude near-midline values
                if (entry.pieceTilt < -MIDLINE_THRESHOLD) ptP1Count++;
                else if (entry.pieceTilt > MIDLINE_THRESHOLD) ptP2Count++;
            }
        }

        const fpTotal = fpP1Count + fpP2Count;
        const ptTotal = ptP1Count + ptP2Count;

        return {
            fieldPosition: {
                p1: fpTotal > 0 ? Math.round(100 * fpP1Count / fpTotal) : 0,
                p2: fpTotal > 0 ? Math.round(100 * fpP2Count / fpTotal) : 0,
            },
            pieceTilt: {
                p1: ptTotal > 0 ? Math.round(100 * ptP1Count / ptTotal) : 0,
                p2: ptTotal > 0 ? Math.round(100 * ptP2Count / ptTotal) : 0,
            },
        };
    }, [statsHistory, displayIndex]);

    // Get current value at displayIndex as a percentage (0-100 where 50 is center)
    // Returns 50 for "even" when value is within midline threshold
    const currentValue = useMemo(() => {
        if (!statsHistory || displayIndex < 0 || displayIndex >= statsHistory.length) {
            return null;
        }
        const entry = statsHistory[displayIndex];
        const MIDLINE_THRESHOLD = 0.06;

        let val;
        if (selectedStat === 'fieldPosition') {
            val = entry.fieldPosition;
            if (val === null || val === undefined) return null;
        } else if (selectedStat === 'pieceTilt') {
            // Treat null pieceTilt as 0 (midline) for display
            val = entry.pieceTilt ?? 0;
        } else if (selectedStat === 'value') {
            val = entry.value;
            if (val === null || val === undefined) return null;
        } else {
            return null;
        }

        // If within midline threshold, treat as even (50)
        if (Math.abs(val) <= MIDLINE_THRESHOLD) {
            return 50;
        }

        // Convert from -1..1 to 0..100 (P1 advantage = 0, P2 advantage = 100)
        return Math.round(((val + 1) / 2) * 100);
    }, [statsHistory, displayIndex, selectedStat]);

    // Get index from x position
    const getIndexFromX = useCallback((clientX) => {
        if (!svgRef.current || numPoints < 2) return 0;
        const rect = svgRef.current.getBoundingClientRect();
        const x = clientX - rect.left - padding.left;
        const index = Math.round(x / xStep);
        return Math.max(0, Math.min(numPoints - 1, index));
    }, [numPoints, xStep, padding.left]);

    // Mouse/touch handlers - just for scrubbing the indicator, no navigation
    const handlePointerDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
        const index = getIndexFromX(e.clientX);
        setHoverIndex(index);
    }, [getIndexFromX]);

    const handlePointerMove = useCallback((e) => {
        if (!isDragging) return;
        const index = getIndexFromX(e.clientX);
        if (index !== hoverIndex) {
            setHoverIndex(index);
        }
    }, [isDragging, getIndexFromX, hoverIndex]);

    const handlePointerUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handlePointerLeave = useCallback(() => {
        if (!isDragging) {
            setHoverIndex(null);
        }
    }, [isDragging]);

    // Get indicator position
    const getIndicatorY = useCallback((index) => {
        let points;
        if (selectedStat === 'fieldPosition') {
            points = fpPoints;
        } else if (selectedStat === 'pieceTilt') {
            points = ptPoints;
        } else if (selectedStat === 'value') {
            points = valuePoints;
        } else {
            points = fpPoints;
        }

        if (!points[index] || points[index].y === null) {
            return padding.top + chartHeight / 2;
        }
        return points[index].y;
    }, [selectedStat, fpPoints, ptPoints, valuePoints, padding.top, chartHeight]);

    if (!hasData) {
        return (
            <div className="stats-plot-empty">
                <span>No game data yet</span>
            </div>
        );
    }

    const centerY = padding.top + chartHeight / 2;
    const indicatorX = padding.left + displayIndex * xStep;
    const indicatorY = getIndicatorY(displayIndex);

    return (
        <div className="stats-plot-container">
            <svg
                ref={svgRef}
                width={width}
                height={height}
                className={`stats-plot-svg interactive ${isDragging ? 'dragging' : ''}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerLeave}
            >
                {/* Background */}
                <rect
                    x={padding.left}
                    y={padding.top}
                    width={chartWidth}
                    height={chartHeight}
                    className="stats-plot-bg"
                />

                {/* Center line (50%) */}
                <line
                    x1={padding.left}
                    y1={centerY}
                    x2={padding.left + chartWidth}
                    y2={centerY}
                    className="stats-plot-center-line"
                />

                {/* Field Position line */}
                {fieldPositionPath && (
                    <path
                        d={fieldPositionPath}
                        className={`stats-plot-line field-position ${selectedStat !== 'fieldPosition' ? 'dimmed' : ''}`}
                        fill="none"
                    />
                )}

                {/* Piece Tilt line */}
                {pieceTiltPath && (
                    <path
                        d={pieceTiltPath}
                        className={`stats-plot-line piece-tilt ${selectedStat !== 'pieceTilt' ? 'dimmed' : ''}`}
                        fill="none"
                    />
                )}

                {/* Value (Winning Probability) line */}
                {valuePath && (
                    <path
                        d={valuePath}
                        className={`stats-plot-line value ${selectedStat !== 'value' ? 'dimmed' : ''}`}
                        fill="none"
                    />
                )}

                {/* Scrubber vertical line */}
                <line
                    x1={indicatorX}
                    y1={padding.top}
                    x2={indicatorX}
                    y2={padding.top + chartHeight}
                    className="stats-plot-scrubber-line"
                />

                {/* Indicator ball */}
                <circle
                    cx={indicatorX}
                    cy={indicatorY}
                    r={6}
                    className={`stats-plot-indicator ${selectedStat}`}
                />

                {/* Y-axis labels */}
                <text x={padding.left - 6} y={padding.top + 6} className="stats-plot-label p1-label">P1</text>
                <text x={padding.left - 6} y={padding.top + chartHeight - 2} className="stats-plot-label p2-label">P2</text>

                {/* X-axis label */}
                <text
                    x={padding.left + chartWidth / 2}
                    y={height - 6}
                    className="stats-plot-label x-label"
                >
                    moves
                </text>
            </svg>

            {/* Move indicator and legend */}
            <div className="stats-plot-footer">
                <div className="stats-plot-move-indicator">
                    <span className="move-current">{displayIndex}</span>
                    <span className="move-separator">/</span>
                    <span className="move-total">{numPoints - 1}</span>
                    {currentValue !== null && (
                        <span className="move-value">
                            ({currentValue < 50 ? `P1 ${50 - currentValue}%` : currentValue > 50 ? `P2 ${currentValue - 50}%` : 'even'})
                        </span>
                    )}
                </div>

                {/* Legend - clickable to select stat */}
                <div className="stats-plot-legend">
                    <button
                        className={`legend-item ${selectedStat === 'fieldPosition' ? 'selected' : ''}`}
                        onClick={(e) => { setSelectedStat('fieldPosition'); e.currentTarget.blur(); }}
                    >
                        <span className="legend-swatch field-position"></span>
                        <span className="legend-text">Field Position</span>
                    </button>
                    <button
                        className={`legend-item ${selectedStat === 'pieceTilt' ? 'selected' : ''}`}
                        onClick={(e) => { setSelectedStat('pieceTilt'); e.currentTarget.blur(); }}
                    >
                        <span className="legend-swatch piece-tilt"></span>
                        <span className="legend-text">Piece Tilt</span>
                    </button>
                    <button
                        className={`legend-item ${selectedStat === 'value' ? 'selected' : ''}`}
                        onClick={(e) => { setSelectedStat('value'); e.currentTarget.blur(); }}
                    >
                        <span className="legend-swatch value"></span>
                        <span className="legend-text">Win Probability</span>
                    </button>
                </div>
            </div>

            {/* Cumulative stats bars */}
            {cumulativeStats && (
                <div className="stats-plot-cumulative">
                    <span className="cumulative-title">CUMULATIVE</span>
                    <div className="cumulative-stat-row">
                        <span className="cumulative-label">Field Pos</span>
                        <div className="cumulative-bar-container">
                            <div
                                className="cumulative-bar p1"
                                style={{ width: `${cumulativeStats.fieldPosition.p1}%` }}
                            />
                            <div
                                className="cumulative-bar p2"
                                style={{ width: `${cumulativeStats.fieldPosition.p2}%` }}
                            />
                        </div>
                        <div className="cumulative-values">
                            <span className="cumulative-p1">{cumulativeStats.fieldPosition.p1}%</span>
                            <span className="cumulative-p2">{cumulativeStats.fieldPosition.p2}%</span>
                        </div>
                    </div>
                    <div className="cumulative-stat-row">
                        <span className="cumulative-label">Piece Tilt</span>
                        <div className="cumulative-bar-container">
                            <div
                                className="cumulative-bar p1"
                                style={{ width: `${cumulativeStats.pieceTilt.p1}%` }}
                            />
                            <div
                                className="cumulative-bar p2"
                                style={{ width: `${cumulativeStats.pieceTilt.p2}%` }}
                            />
                        </div>
                        <div className="cumulative-values">
                            <span className="cumulative-p1">{cumulativeStats.pieceTilt.p1}%</span>
                            <span className="cumulative-p2">{cumulativeStats.pieceTilt.p2}%</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatsPlot;
